import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Avatar from 'antd/lib/avatar';
import Button from 'antd/lib/button';
import Dropdown from 'antd/lib/dropdown';
import Paragraph from 'antd/lib/typography/Paragraph';
import Text from 'antd/lib/typography/Text';
import Tooltip from 'antd/lib/tooltip';
import {
    BugOutlined,
    BulbFilled,
    CaretDownOutlined,
    CheckCircleFilled,
    CheckCircleOutlined,
    CheckOutlined,
    ClockCircleFilled,
    CloseCircleFilled,
    CloseCircleOutlined,
    CloseOutlined,
    EllipsisOutlined,
    InfoCircleOutlined,
    LineChartOutlined,
    LoadingOutlined,
    MessageOutlined,
    MonitorOutlined,
    PlusCircleOutlined,
    RobotOutlined,
    SendOutlined,
    StarOutlined,
    TagOutlined,
    TeamOutlined,
    ToolFilled,
    ToolOutlined,
    UndoOutlined,
} from '@ant-design/icons';
import {
    antGold,
    antGrey,
    antMagenta,
    colorWithAlpha,
    getAvatarColorForName,
    runwayColors,
    rwColors,
} from '@/components/styles/globalStyles';
import IntegrationIcon from '@/components/icons/IntegrationIcon';
import { IssueEntity, IssueSourceType, IssueState, OrgUser } from '@/types';
import { RootState } from '@/reducers/reducers';
import {
    selectAppTimeFormatSetting,
    selectEffectiveTimezone,
    selectReleasesForAppId,
    selectUserById,
} from '@/selectors/selectors';
import { formatDateWithTimezone } from '@/timeHelpers';
import { selectAppOrgUsers, selectSortedGroups } from '@/selectors/memoizedSelectors';
import {
    TRY_REOPEN_ISSUE,
    TRY_TRANSITION_ISSUE,
    TRY_UPDATE_ISSUE,
    TRY_UPDATE_ISSUE_ASSIGNEES,
    TriageViewType,
    tryReopenIssue,
    tryTransitionIssue,
    tryUpdateIssue,
    tryUpdateIssueAssignees,
} from '@/actions/triage';
import { displayTextForUserChecklistGroup, displayUserNameLong } from '@/helpers/displayHelpers';
import { useIsLoading } from '@/utils/hooks/helpers';
import { RequestFixModal } from './RequestFixModal';
import { TriageIssueDrawer } from './TriageIssueDrawer';

const stopCardClick = (e: React.MouseEvent) => e.stopPropagation();

export const LABEL_STYLE: React.CSSProperties = {
    color: antGrey.grey8,
    fontSize: 10,
    fontWeight: 600,
    letterSpacing: 1,
    textTransform: 'uppercase',
};

const UserAvatarWithTooltip: React.FC<{
    user?: OrgUser;
    fallbackName?: string;
    size?: number;
    extraTooltip?: string;
    bordered?: boolean;
}> = ({ user, fallbackName, size = 28, extraTooltip, bordered }) => {
    const name = user ? displayUserNameLong(user) : (fallbackName ?? '');
    const bgColor = getAvatarColorForName(name);
    const tooltip =
        extraTooltip ?
            <>
                {name ?
                    <>
                        {name}
                        <br />
                    </>
                :   null}
                {extraTooltip}
            </>
        :   name || undefined;
    const avatar = (
        <Avatar
            size={size}
            src={user?.commsAvatars?.image32}
            style={{ backgroundColor: bgColor, flexShrink: 0, fontSize: 12, border: 'none' }}
        >
            {name.charAt(0).toUpperCase()}
        </Avatar>
    );
    return (
        <Tooltip title={tooltip} placement="top">
            {bordered ?
                <span
                    style={{
                        display: 'inline-flex',
                        borderRadius: '50%',
                        padding: 2,
                        background: `repeating-conic-gradient(${bgColor} 0deg 6deg, transparent 6deg 15deg)`,
                    }}
                >
                    {avatar}
                </span>
            :   avatar}
        </Tooltip>
    );
};

// Placeholder source-type icons — to be swapped for Matt's per-source icons.
const SOURCE_TYPE_ICON: Record<IssueSourceType, React.ReactNode> = {
    [IssueSourceType.Runway]: <MessageOutlined />,
    [IssueSourceType.PMTicket]: <TagOutlined />,
    [IssueSourceType.UserReviews]: <StarOutlined />,
    [IssueSourceType.BetaFeedback]: <TeamOutlined />,
    [IssueSourceType.RegressionTesting]: <BugOutlined />,
    [IssueSourceType.HealthMetrics]: <LineChartOutlined />,
};

interface StateMeta {
    label: string;
    icon: React.ReactNode;
}

export const stateMeta = (state: IssueState): StateMeta => {
    switch (state) {
        case IssueState.New:
            return { label: 'Needs triage', icon: <SendOutlined style={{ color: rwColors.primary, fontSize: 16 }} /> };
        case IssueState.Assigned:
            return {
                label: 'Assigned',
                icon: <MonitorOutlined style={{ color: rwColors.statusYellow, fontSize: 16 }} />,
            };
        case IssueState.FixRequested:
            return {
                label: 'Fix requested',
                icon: <ToolFilled style={{ color: rwColors.statusYellow, fontSize: 16 }} />,
            };
        case IssueState.FixApproved:
            return {
                label: 'Fix approved',
                icon: <CheckCircleFilled style={{ color: rwColors.statusGreen, fontSize: 16 }} />,
            };
        case IssueState.Reopened:
            return {
                label: 'Reopened',
                icon: <UndoOutlined style={{ color: rwColors.statusYellow, fontSize: 16 }} />,
            };
        case IssueState.FixMerged:
            return {
                label: 'Resolved',
                icon: <CheckCircleOutlined style={{ color: rwColors.statusGreen, fontSize: 16 }} />,
            };
        case IssueState.FixRejected:
            return {
                label: 'Fix rejected',
                icon: <CloseCircleFilled style={{ color: rwColors.statusRed, fontSize: 16 }} />,
            };
        case IssueState.NotAPriority:
        default:
            return {
                label: 'Not a priority',
                icon: <CloseCircleOutlined style={{ color: antGrey.grey6, fontSize: 16 }} />,
            };
    }
};

export const TriageIssueCard: React.FC<{
    issue: IssueEntity;
    orgId: string;
    preview?: boolean;
    viewType: TriageViewType;
}> = ({ issue, orgId, preview, viewType }) => {
    const [drawerOpen, setDrawerOpen] = React.useState(false);
    const hasFixRequest = !!issue.fixRequest;
    // Dim the whole card for deprioritized / rejected issues to signal they won't be acted on.
    const isDimmed = issue.state === IssueState.FixRejected || issue.state === IssueState.NotAPriority;
    return (
        <>
            <div
                onClick={preview ? undefined : () => setDrawerOpen(true)}
                style={{
                    border: `1px solid ${antGrey.grey5}`,
                    borderRadius: 8,
                    overflow: 'hidden',
                    backgroundColor: antGrey.grey2,
                    opacity: isDimmed ? 0.6 : undefined,
                    cursor: preview ? undefined : 'pointer',
                }}
            >
                {hasFixRequest && !preview && <FixRequestModule issue={issue} orgId={orgId} />}
                <IssueBody issue={issue} orgId={orgId} preview={preview} viewType={viewType} />
            </div>
            {!preview && (
                <TriageIssueDrawer
                    open={drawerOpen}
                    onClose={() => setDrawerOpen(false)}
                    orgId={orgId}
                    issue={issue}
                    viewType={viewType}
                />
            )}
        </>
    );
};

interface FixRequestStatusMeta {
    label: string;
    icon: React.ReactNode;
    pending: boolean;
}

export const fixRequestStatusMeta = (state: IssueState): FixRequestStatusMeta => {
    switch (state) {
        case IssueState.FixApproved:
            return {
                label: 'Fix approved',
                icon: <CheckCircleFilled style={{ color: rwColors.statusGreen, fontSize: 16 }} />,
                pending: false,
            };
        case IssueState.FixMerged:
            return {
                label: 'Fix merged',
                icon: <CheckCircleFilled style={{ color: rwColors.statusGreen, fontSize: 16 }} />,
                pending: false,
            };
        case IssueState.FixRejected:
            return {
                label: 'Fix rejected',
                icon: <CloseCircleFilled style={{ color: rwColors.statusRed, fontSize: 16 }} />,
                pending: false,
            };
        case IssueState.FixRequested:
        default:
            return {
                label: 'Pending approval',
                icon: <ClockCircleFilled style={{ color: rwColors.statusYellow, fontSize: 16 }} />,
                pending: state === IssueState.FixRequested,
            };
    }
};

const FixRequestModule: React.FC<{ issue: IssueEntity; orgId: string }> = ({ issue, orgId }) => {
    const dispatch = useDispatch();
    const fixRequest = issue.fixRequest;
    const meta = fixRequestStatusMeta(issue.state);
    const requesterId = fixRequest?.requestedByUserId;
    const requester = useSelector((state: RootState) =>
        requesterId ? selectUserById(state, orgId, issue.appId, requesterId) : undefined,
    );
    const deciderId = fixRequest?.decidedByUserId;
    const decider = useSelector((state: RootState) =>
        deciderId ? selectUserById(state, orgId, issue.appId, deciderId) : undefined,
    );
    const isRejected = issue.state === IssueState.FixRejected;
    const showDecided =
        !!deciderId && (isRejected || issue.state === IssueState.FixApproved || issue.state === IssueState.FixMerged);
    const effectiveTimezone = useSelector((state: RootState) => selectEffectiveTimezone(state, issue.appId));
    const use24HourTimeFormat = useSelector((state: RootState) => selectAppTimeFormatSetting(state, issue.appId));
    const decidedAtLabel =
        fixRequest?.decidedAt ?
            formatDateWithTimezone(
                fixRequest.decidedAt,
                'longMonthDateYearTime',
                effectiveTimezone,
                use24HourTimeFormat,
            )
        :   undefined;
    const isTransitioning = useIsLoading(TRY_TRANSITION_ISSUE, issue.id);
    const transition = (targetState: IssueState) =>
        dispatch(tryTransitionIssue({ orgId, appId: issue.appId, issueId: issue.id, targetState }));
    const stop = (e: React.MouseEvent) => e.stopPropagation();

    return (
        <div
            style={{
                display: 'flex',
                alignItems: 'center',
                gap: 16,
                padding: '12px 16px',
                backgroundColor: antGrey.grey3,
                borderBottom: `1px solid ${antGrey.grey5}`,
                cursor: 'pointer',
            }}
        >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, width: 240, flexShrink: 0 }}>
                {meta.icon}
                <Text style={LABEL_STYLE}>{meta.label}</Text>
            </div>
            {requesterId && <UserAvatarWithTooltip user={requester} fallbackName={requesterId} size={20} />}
            <div style={{ flex: 1, minWidth: 0 }}>
                {fixRequest?.details && (
                    <Text ellipsis style={{ color: antGrey.grey8, fontSize: 13 }}>
                        {fixRequest.details}
                    </Text>
                )}
            </div>
            {meta.pending && (
                <div style={{ display: 'flex', gap: 8 }} onClick={stop}>
                    <Tooltip title="Approve fix" placement="top">
                        <Button
                            size="small"
                            shape="circle"
                            loading={isTransitioning}
                            onClick={() => transition(IssueState.FixApproved)}
                            icon={
                                isTransitioning ? undefined : (
                                    <CheckOutlined style={{ color: antGrey.grey6, fontSize: 11 }} />
                                )
                            }
                            style={{
                                backgroundColor: runwayColors.white,
                                borderColor: antGrey.grey6,
                                width: 20,
                                height: 20,
                                minWidth: 20,
                            }}
                        />
                    </Tooltip>
                    <Tooltip title="Reject fix" placement="top">
                        <Button
                            size="small"
                            shape="circle"
                            onClick={() => transition(IssueState.FixRejected)}
                            icon={<CloseOutlined style={{ color: antGrey.grey6, fontSize: 10 }} />}
                            style={{
                                backgroundColor: runwayColors.white,
                                borderColor: antGrey.grey6,
                                width: 20,
                                height: 20,
                                minWidth: 20,
                            }}
                        />
                    </Tooltip>
                </div>
            )}
            {showDecided && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span
                        style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 20,
                            height: 20,
                            borderRadius: '50%',
                            border: `1px solid ${isRejected ? rwColors.statusRed : rwColors.statusGreen}`,
                        }}
                    >
                        {isRejected ?
                            <CloseOutlined style={{ color: rwColors.statusRed, fontSize: 10 }} />
                        :   <CheckOutlined style={{ color: rwColors.statusGreen, fontSize: 11 }} />}
                    </span>
                    <UserAvatarWithTooltip
                        user={decider}
                        fallbackName={deciderId}
                        size={20}
                        extraTooltip={decidedAtLabel}
                    />
                </div>
            )}
        </div>
    );
};

const IssueBody: React.FC<{ issue: IssueEntity; orgId: string; preview?: boolean; viewType: TriageViewType }> = ({
    issue,
    orgId,
    preview,
    viewType,
}) => {
    const hasReporter = !!issue.reporter;
    return (
        <div style={{ display: 'flex', backgroundColor: antGrey.grey2 }}>
            <div style={{ flex: 1, display: 'flex', gap: 16, padding: 16, backgroundColor: runwayColors.white }}>
                <StatusColumn issue={issue} />
                <ContentColumn issue={issue} />
                {hasReporter && !preview && <ReporterSection issue={issue} orgId={orgId} />}
                <AssigneeSection issue={issue} orgId={orgId} preview={preview} viewType={viewType} />
            </div>
            {!preview && (
                <div
                    onClick={stopCardClick}
                    style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        padding: '16px 10px 16px 16px',
                        backgroundColor: antGrey.grey3,
                    }}
                >
                    <VersionColumn issue={issue} orgId={orgId} />
                    <ActionsColumn issue={issue} orgId={orgId} viewType={viewType} />
                </div>
            )}
        </div>
    );
};

const StatusColumn: React.FC<{ issue: IssueEntity }> = ({ issue }) => {
    const meta = stateMeta(issue.state);
    const sources = issue.sources ?? [];
    const distinctSourceTypes = Array.from(new Set(sources.map((s) => s.type)));
    const hasPotentialCause = !!issue.potentialCauses?.length;
    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 12,
                width: 200,
                flexShrink: 0,
                padding: 16,
                margin: '-16px 0 -16px -16px',
                backgroundColor: antGrey.grey2,
            }}
        >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {meta.icon}
                <Text style={LABEL_STYLE}>{meta.label}</Text>
                {hasPotentialCause && (
                    <span
                        style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            marginLeft: 'auto',
                            padding: 3,
                            borderRadius: 2,
                            backgroundColor: antGold.gold5,
                        }}
                    >
                        <BulbFilled style={{ color: runwayColors.white, fontSize: 14 }} />
                    </span>
                )}
            </div>
            {sources.length > 0 && (
                <div
                    style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 6,
                        padding: '4px',
                        border: `1px solid ${antGrey.grey6}`,
                        borderRadius: 4,
                        backgroundColor: antGrey.grey1,
                        alignSelf: 'flex-start',
                        marginTop: 'auto',
                        color: antGrey.grey6,
                        fontSize: 14,
                    }}
                >
                    {sources.length > 1 && (
                        <Text style={{ fontSize: 16, color: antGrey.grey6, fontWeight: 600 }}>{sources.length}x</Text>
                    )}
                    {distinctSourceTypes.map((type) => (
                        <span key={type} style={{ display: 'inline-flex' }}>
                            {SOURCE_TYPE_ICON[type]}
                        </span>
                    ))}
                </div>
            )}
        </div>
    );
};

const ContentColumn: React.FC<{ issue: IssueEntity }> = ({ issue }) => {
    const source = issue.sources?.[0];
    const description = issue.description;
    const title = issue.title;
    return (
        <div
            style={{
                flex: 1,
                minWidth: 0,
                display: 'flex',
                gap: 16,
                padding: 16,
                margin: '-16px 0',
                backgroundColor: runwayColors.white,
            }}
        >
            <div style={{ flexShrink: 0, lineHeight: 0 }}>
                <IntegrationIcon integrationId={source?.integrationType} size={24} />
            </div>
            <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 12 }}>
                {title && (
                    <Text style={{ fontSize: 15, fontWeight: 500, color: antGrey.grey10, wordBreak: 'break-word' }}>
                        {title}
                    </Text>
                )}
                {description && (
                    <Paragraph
                        style={{ color: antGrey.grey7, fontSize: 13, wordBreak: 'break-word', marginBottom: 0 }}
                        ellipsis={{ rows: 5, tooltip: description }}
                    >
                        {description}
                    </Paragraph>
                )}
            </div>
        </div>
    );
};

const ColumnHeader: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <Text style={{ ...LABEL_STYLE, fontSize: 11 }}>{children}</Text>
);

const ReporterSection: React.FC<{ issue: IssueEntity; orgId: string }> = ({ issue, orgId }) => {
    // issue.reporter is a userId; fall back to the source's free-form reporter string when unresolved.
    const reporter = useSelector((state: RootState) =>
        issue.reporter ? selectUserById(state, orgId, issue.appId, issue.reporter) : undefined,
    );
    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: 16,
                width: 100,
                flexShrink: 0,
                padding: '16px 8px',
                margin: '-16px 0',
                backgroundColor: colorWithAlpha(antGrey.grey2, 0.5),
            }}
        >
            <ColumnHeader>Reporter</ColumnHeader>
            <UserAvatarWithTooltip user={reporter} fallbackName={issue.reporter} />
        </div>
    );
};

const ASSIGNEE_USER_PREFIX = 'user:';
const ASSIGNEE_GROUP_PREFIX = 'group:';

const AssigneeSection: React.FC<{ issue: IssueEntity; orgId: string; preview?: boolean; viewType: TriageViewType }> = ({
    issue,
    orgId,
    preview,
    viewType,
}) => {
    const dispatch = useDispatch();
    const assigneeId = issue.assigneeUserIds?.[0];
    const assigneeGroupId = issue.assigneeGroupIds?.[0];
    const suggestedId = issue.suggestedAssigneeUserIds?.[0];
    const assignee = useSelector((state: RootState) =>
        assigneeId ? selectUserById(state, orgId, issue.appId, assigneeId) : undefined,
    );
    const appUsers = useSelector((state: RootState) => selectAppOrgUsers(state, issue.appId));
    const groups = useSelector((state: RootState) => selectSortedGroups(state, orgId));
    const assignedGroup = assigneeGroupId ? groups.find((g) => g.id === assigneeGroupId) : undefined;
    const isAssigning = useIsLoading(TRY_UPDATE_ISSUE_ASSIGNEES, issue.id);

    const assign = (assigneeUserIds: string[], assigneeGroupIds: string[]) =>
        dispatch(
            tryUpdateIssueAssignees({
                orgId,
                appId: issue.appId,
                issueId: issue.id,
                assigneeUserIds,
                assigneeGroupIds,
                viewType,
            }),
        );

    const onAssigneeSelected = (key: string) => {
        if (key.startsWith(ASSIGNEE_GROUP_PREFIX)) {
            assign([], [key.slice(ASSIGNEE_GROUP_PREFIX.length)]);
        } else {
            assign([key.slice(ASSIGNEE_USER_PREFIX.length)], []);
        }
    };

    const menuLabel = (text: string) => (
        <span
            style={{
                display: 'block',
                maxWidth: 150,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                fontSize: 15,
                color: antGrey.grey8,
            }}
        >
            {text}
        </span>
    );

    const assigneeMenuItems = [
        ...appUsers.map((user) => {
            const name = displayUserNameLong(user);
            return {
                key: `${ASSIGNEE_USER_PREFIX}${user.id}`,
                label: menuLabel(name),
                icon: (
                    <Avatar
                        size={28}
                        src={user.commsAvatars?.image32}
                        style={{ backgroundColor: getAvatarColorForName(name), fontSize: 12, border: 'none' }}
                    >
                        {name.charAt(0).toUpperCase()}
                    </Avatar>
                ),
            };
        }),
        ...groups.map((group) => ({
            key: `${ASSIGNEE_GROUP_PREFIX}${group.id}`,
            label: menuLabel(displayTextForUserChecklistGroup(group)),
            icon: (
                <Avatar
                    size={28}
                    icon={<TeamOutlined />}
                    style={{ backgroundColor: antMagenta.magenta5, fontSize: 12, border: 'none' }}
                />
            ),
        })),
    ];

    const selectedAssigneeKey =
        assigneeId ? `${ASSIGNEE_USER_PREFIX}${assigneeId}`
        : assigneeGroupId ? `${ASSIGNEE_GROUP_PREFIX}${assigneeGroupId}`
        : undefined;

    let avatar: React.ReactNode;
    if (assigneeId) {
        avatar = <UserAvatarWithTooltip user={assignee} fallbackName={assigneeId} bordered={issue.isAutoAssigned} />;
    } else if (assigneeGroupId) {
        avatar = (
            <Tooltip
                title={assignedGroup ? displayTextForUserChecklistGroup(assignedGroup) : assigneeGroupId}
                placement="top"
            >
                <Avatar
                    size={28}
                    style={{ backgroundColor: antMagenta.magenta5, fontSize: 12 }}
                    icon={<TeamOutlined />}
                />
            </Tooltip>
        );
    } else if (suggestedId) {
        avatar = (
            <Tooltip title="Suggested assignee" placement="top">
                <Avatar size={28} style={{ backgroundColor: rwColors.statusRed }} icon={<RobotOutlined />} />
            </Tooltip>
        );
    } else {
        avatar = (
            <span
                style={{
                    width: 28,
                    height: 28,
                    boxSizing: 'border-box',
                    borderRadius: '50%',
                    border: `1.5px dashed ${antGrey.grey6}`,
                    backgroundColor: runwayColors.white,
                    display: 'inline-block',
                }}
            />
        );
    }

    return (
        <div
            onClick={stopCardClick}
            style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: 16,
                width: 100,
                flexShrink: 0,
                padding: '16px 8px',
                margin: '-16px -16px -16px -16px',
                backgroundColor: antGrey.grey2,
            }}
        >
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <ColumnHeader>Assignee</ColumnHeader>
                {issue.isAutoAssigned && (
                    <Tooltip title="Auto-assigned by Runway" placement="top">
                        <InfoCircleOutlined style={{ color: antGrey.grey6, fontSize: 12 }} />
                    </Tooltip>
                )}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 24 }}>
                {avatar}
                {preview ?
                    null
                : isAssigning ?
                    <LoadingOutlined spin style={{ color: antGrey.grey6, fontSize: 18 }} />
                :   <Dropdown
                        trigger={['click']}
                        placement="bottomRight"
                        menu={{
                            items: assigneeMenuItems,
                            selectedKeys: selectedAssigneeKey ? [selectedAssigneeKey] : [],
                            onClick: ({ key }) => onAssigneeSelected(key),
                            style: { maxHeight: 320, overflowY: 'auto' },
                        }}
                    >
                        <CaretDownOutlined style={{ color: antGrey.grey5, fontSize: 22, cursor: 'pointer' }} />
                    </Dropdown>
                }
            </div>
        </div>
    );
};

const BuildConnector: React.FC = () => {
    const dot = <span style={{ width: 7, height: 7, borderRadius: '50%', backgroundColor: antGrey.grey5 }} />;
    return (
        <div
            style={{
                position: 'absolute',
                left: -14,
                top: '50%',
                transform: 'translateY(-50%)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
            }}
        >
            {dot}
            <span style={{ height: 20, width: 1, backgroundColor: antGrey.grey5 }} />
            {dot}
        </div>
    );
};

const BuildRow: React.FC<{ value?: string; dashedBottom?: boolean }> = ({ value, dashedBottom }) => (
    <div
        style={{
            padding: '4px 10px',
            borderBottom: dashedBottom ? `1px dashed ${antGrey.grey5}` : undefined,
        }}
    >
        <Text style={{ color: value ? antGrey.grey9 : antGrey.grey6, fontSize: 13 }}>{value ? `#${value}` : '--'}</Text>
    </div>
);

const VersionColumn: React.FC<{ issue: IssueEntity; orgId: string }> = ({ issue, orgId }) => {
    const dispatch = useDispatch();
    const version = issue.versions?.[0];
    const build = issue.builds?.[0];
    const releases = useSelector((state: RootState) => selectReleasesForAppId(state, issue.appId));
    const isUpdating = useIsLoading(TRY_UPDATE_ISSUE, issue.id);

    const versionMenuItems = releases.map((release) => ({
        key: release.version,
        label: (
            <span style={{ color: antGrey.grey8, fontSize: 14 }}>{release.displayableVersion || release.version}</span>
        ),
    }));

    const selectVersion = (newVersion: string) =>
        dispatch(tryUpdateIssue({ orgId, appId: issue.appId, issueId: issue.id, version: newVersion }));

    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 6,
                width: 128,
                flexShrink: 0,
            }}
        >
            <Dropdown
                trigger={['click']}
                placement="bottomRight"
                menu={{
                    items: versionMenuItems,
                    selectedKeys: version ? [version] : [],
                    onClick: ({ key }) => selectVersion(key),
                    style: { maxHeight: 320, overflowY: 'auto' },
                }}
            >
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        width: 104,
                        backgroundColor: antGrey.grey3,
                        border: `1px solid ${antGrey.grey5}`,
                        borderRadius: 6,
                        padding: '4px 10px',
                        cursor: 'pointer',
                    }}
                >
                    <Text style={{ color: version ? antGrey.grey8 : antGrey.grey6, fontWeight: 600, fontSize: 12 }}>
                        {version ?? '--'}
                    </Text>
                    {isUpdating ?
                        <LoadingOutlined spin style={{ color: antGrey.grey6, fontSize: 16 }} />
                    :   <CaretDownOutlined style={{ color: antGrey.grey6, fontSize: 16 }} />}
                </div>
            </Dropdown>
            {/* Build box — two slots; dashed when empty, solid once a build is present.
                TODO: second slot (CI vs store build) needs a dedicated BE field. */}
            <div style={{ position: 'relative', width: 104 }}>
                <BuildConnector />
                <div
                    style={{
                        backgroundColor: runwayColors.white,
                        border: `1px ${build ? 'solid' : 'dashed'} ${antGrey.grey5}`,
                        borderRadius: 6,
                        overflow: 'hidden',
                    }}
                >
                    <BuildRow value={build} dashedBottom />
                    <BuildRow />
                </div>
            </div>
        </div>
    );
};

const CLOSED_STATES = [IssueState.FixMerged, IssueState.FixRejected, IssueState.NotAPriority];

// Menu key for "Request approval for a fix" — not a transition (opens a modal, wired in a later PR).
const REQUEST_FIX_KEY = 'requestFix';
const ADD_SOURCE_KEY = 'addSource';

const ActionsColumn: React.FC<{ issue: IssueEntity; orgId: string; viewType: TriageViewType }> = ({
    issue,
    orgId,
    viewType,
}) => {
    const dispatch = useDispatch();
    const isAssigned = (issue.assigneeUserIds?.length ?? 0) > 0;
    const isClosed = CLOSED_STATES.includes(issue.state);
    const isTransitioning = useIsLoading(TRY_TRANSITION_ISSUE, issue.id);
    const isReopening = useIsLoading(TRY_REOPEN_ISSUE, issue.id);
    const [requestFixOpen, setRequestFixOpen] = React.useState(false);

    const transition = (targetState: IssueState) =>
        dispatch(tryTransitionIssue({ orgId, appId: issue.appId, issueId: issue.id, targetState, viewType }));
    const reopen = () => dispatch(tryReopenIssue({ orgId, appId: issue.appId, issueId: issue.id, viewType }));

    // TODO RUN-15475 permission: gate approve/reject enabled state on the approve permission (separate ticket).
    const menuLabel = (text: string) => <span style={{ color: antGrey.grey7 }}>{text}</span>;
    let fixMenuItems;
    if (!issue.fixRequest) {
        fixMenuItems = [
            {
                key: REQUEST_FIX_KEY,
                icon: <ToolOutlined style={{ color: antGrey.grey7 }} />,
                label: menuLabel('Request approval for a fix'),
            },
        ];
    } else if (issue.state === IssueState.FixRequested) {
        fixMenuItems = [
            {
                key: IssueState.FixApproved,
                icon: <CheckCircleOutlined style={{ color: antGrey.grey7 }} />,
                label: menuLabel('Approve fix'),
            },
            {
                key: IssueState.FixRejected,
                icon: <CloseCircleOutlined style={{ color: antGrey.grey7 }} />,
                label: menuLabel('Reject fix'),
            },
        ];
    } else {
        fixMenuItems = [];
    }
    const menuItems =
        isClosed ?
            []
        :   [
                ...fixMenuItems,
                {
                    key: ADD_SOURCE_KEY,
                    icon: <PlusCircleOutlined style={{ color: antGrey.grey7 }} />,
                    label: menuLabel('Add a source...'),
                },
                {
                    key: IssueState.NotAPriority,
                    icon: <CloseCircleOutlined style={{ color: antGrey.grey7 }} />,
                    label: menuLabel("Mark as 'Not a priority'"),
                },
                ...(issue.state === IssueState.FixApproved ?
                    [
                        {
                            key: IssueState.FixMerged,
                            icon: <CheckCircleOutlined style={{ color: antGrey.grey7 }} />,
                            label: menuLabel("Mark as 'Resolved'"),
                        },
                    ]
                :   []),
            ];
    const onMenuClick = ({ key }: { key: string }) => {
        if (key === REQUEST_FIX_KEY) {
            setRequestFixOpen(true);
            return;
        }
        if (key === ADD_SOURCE_KEY) {
            // TODO RUN-16408: open the add-source modal. Separate PR.
            return;
        }
        // Every other key is the target IssueState.
        transition(key as IssueState);
    };

    return (
        <>
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-end',
                    justifyContent: 'flex-start',
                    alignSelf: 'stretch',
                    gap: 20,
                    paddingTop: 14,
                    width: 40,
                    flexShrink: 0,
                }}
            >
                {isClosed ?
                    <Tooltip title="Reopen" placement="top">
                        <Button
                            size="small"
                            shape="circle"
                            loading={isReopening}
                            onClick={reopen}
                            icon={isReopening ? undefined : <UndoOutlined style={{ color: antGrey.grey6 }} />}
                            style={{ backgroundColor: 'transparent', borderColor: colorWithAlpha(antGrey.grey6, 0.5) }}
                        />
                    </Tooltip>
                : !isAssigned ?
                    <Tooltip title="Mark as 'Not a priority'" placement="top">
                        <Button
                            size="small"
                            shape="circle"
                            loading={isTransitioning}
                            onClick={() => transition(IssueState.NotAPriority)}
                            icon={isTransitioning ? undefined : <CloseOutlined style={{ color: antGrey.grey7 }} />}
                            style={{ backgroundColor: 'transparent', borderColor: colorWithAlpha(antGrey.grey6, 0.5) }}
                        />
                    </Tooltip>
                : !issue.fixRequest ?
                    <Tooltip title="Request approval for a fix" placement="top">
                        <Button
                            size="small"
                            shape="circle"
                            onClick={() => setRequestFixOpen(true)}
                            icon={<ToolOutlined style={{ color: antGrey.grey7 }} />}
                            style={{ backgroundColor: 'transparent', borderColor: colorWithAlpha(antGrey.grey6, 0.5) }}
                        />
                    </Tooltip>
                :   null}
                <Dropdown trigger={['click']} placement="bottomRight" menu={{ items: menuItems, onClick: onMenuClick }}>
                    <Button
                        size="small"
                        type="text"
                        loading={isTransitioning}
                        icon={isTransitioning ? undefined : <EllipsisOutlined style={{ color: antGrey.grey7 }} />}
                    />
                </Dropdown>
            </div>
            <RequestFixModal
                open={requestFixOpen}
                onClose={() => setRequestFixOpen(false)}
                orgId={orgId}
                appId={issue.appId}
                issue={issue}
                viewType={viewType}
            />
        </>
    );
};
